import clearSelectedConversationInModule from 'mini-mail-reading-pane-store/lib/actions/clearSelectedConversationInModule';
import closeItemReadingPane from 'mini-mail-reading-pane-store/lib/actions/closeItemReadingPane';
import closeReadingPane from 'mini-mail-reading-pane-view/lib/utils/closeReadingPane';
import ECViewState from './viewStateHandler';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import Module from 'mini-module-switcher/lib/schema/Module';
import selectFolderWithFallback from '../utils/selectFolderWithFallback';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { FeatureName, isFeatureEnabled } from 'owa-feature-flags';
import { getReadingPaneStore } from 'mini-mail-reading-pane-store/lib/store/Store';
import { getStore as getPreviewPaneStore } from 'mini-preview-pane-store/lib/store/Store';
import { lazyHideBottomSheet } from 'mini-bottom-sheet-animated';
import { lazyHidePreviewPane } from 'mini-preview-pane-view';
import { logUsage } from 'owa-analytics';
import { MAIL_LOAD_READING_PANE_ON_REFRESH_START } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { singleSelectMailItem } from 'mini-mail-list-actions';
import { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';

export interface MailReadingPaneRouteParameters {
    folderId: string;
    conversationId: string;
    itemId?: string;
    immutableItemId?: string;
}

/**
 * Handle folderId and itemId route
 */

export async function mailReadingPaneRouteHandler(params: MailReadingPaneRouteParameters) {
    // When we reach a mail folder route, switch selected module to mail
    switchModule(Module.Mail);

    logUsage('urlDetails', {
        url: window.location.href,
        urlLength: window.location.href.length,
    });

    const { folderId, conversationId, itemId, immutableItemId } = params;

    let encodedFinalItemId;
    if (!!itemId) {
        encodedFinalItemId = encodeURI(itemId);
    }

    //if immutableItemId exists, scrap the itemId value and replace it with immutableItemId
    if (!!immutableItemId && isFeatureEnabled('fwk-immutable-ids' as FeatureName)) {
        encodedFinalItemId = encodeURI(immutableItemId);
    }

    // When reading pane is opened from search view, we needn't fallback to folder view.
    if (getSelectedTableView().tableQuery?.type === TableQueryType.Search) {
        return;
    }

    // Need to await for tableView to be populated
    await selectFolderWithFallback(folderId);

    // For an item view user this is an invalid route, so we terminate.
    if (ECViewState.shouldItemViewBeShown()) {
        return;
    }

    // If we are trying to open an already opened conversation, don't recreate the viewState
    if (
        getReadingPaneStore().conversationReadingPaneViewState &&
        getReadingPaneStore().conversationReadingPaneViewState.conversationId === conversationId
    ) {
        return;
    }

    if (encodedFinalItemId) {
        singleSelectMailItem(conversationId, encodedFinalItemId);
    } else {
        singleSelectMailItem(conversationId);
    }

    signalWorkflowEvent(
        MAIL_LOAD_READING_PANE_ON_REFRESH_START,
        { isItemView: false, itemId: conversationId },
        { isCore: true }
    );
}

/**
 * Clean up when closing item or conversation reading pane view
 */
export async function mailReadingPaneCleanUpRouteHandler(
    oldRoute: string[],
    oldParams: any,
    newRoute: string[],
    newParams: any
): Promise<boolean> {
    if (getPreviewPaneStore().viewState) {
        (await lazyHidePreviewPane.import())();
        return true;
    } else if (getReadingPaneStore().itemReadingPaneViewState) {
        closeItemReadingPane();
        return true;
    } else if ((newParams && newParams.draftId) || (newRoute && newRoute.includes('compose'))) {
        // Bug ID: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/43467
        // On back button press on reading pane, compose should not be opened.
        closeReadingPane();
        return true;
    } else {
        // Don't clear the store if the new route is also a conversation view
        // This is to avoid triggers which adds extra 'mail/<folderId>' routes unnecessarily
        if (!newParams || !newParams.conversationId) {
            clearSelectedConversationInModule();
        }
        lazyHideBottomSheet.importAndExecute();
        return false;
    }
}
